import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import ApprovalKPICards from './components/ApprovalKPICards';
import ApprovalTrendChart from './components/ApprovalTrendChart';
import ResponseTimeChart from './components/ResponseTimeChart';
import RejectionAnalysisPanel from './components/RejectionAnalysisPanel';
import CustomerApprovalTable from './components/CustomerApprovalTable';

const DATE_RANGES = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: '1y', label: '1 year' },
];

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { id: 'trends', label: 'Trends', icon: 'TrendingUp' },
  { id: 'response', label: 'Response Times', icon: 'Timer' },
  { id: 'rejections', label: 'Rejection Analysis', icon: 'XCircle' },
  { id: 'customers', label: 'Customer Analysis', icon: 'Users' },
];

const ApprovalAnalyticsReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');

  const [kpis, setKpis] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [responseTimeData, setResponseTimeData] = useState([]);
  const [rejectionData, setRejectionData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [rawApprovals, setRawApprovals] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const getDateFrom = () => {
    const now = new Date();
    const map = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 };
    const days = map?.[dateRange] ?? 30;
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    return from.toISOString();
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const dateFrom = getDateFrom();

      // Fetch ticket approvals
      const { data: ticketApprovals } = await supabase
        ?.from('ticket_customer_approvals')
        ?.select('id, status, created_at, decided_at, customer_name, customer_notes')
        ?.gte('created_at', dateFrom);

      // Fetch quote approvals
      const { data: quoteApprovals } = await supabase
        ?.from('quote_approval_requests')
        ?.select('id, status, created_at, decided_at, approver_name, decision_notes, quote:quotes(customer:customers(name))')
        ?.gte('created_at', dateFrom);

      const ticketList = (ticketApprovals ?? [])?.map(a => ({
        ...a,
        type: 'ticket',
        customer_name: a?.customer_name ?? 'Unknown',
        rejection_reason: a?.customer_notes,
      }));

      const quoteList = (quoteApprovals ?? [])?.map(a => ({
        ...a,
        type: 'quote',
        customer_name: a?.quote?.customer?.name ?? a?.approver_name ?? 'Unknown',
        rejection_reason: a?.decision_notes,
      }));

      const all = [...ticketList, ...quoteList];
      setRawApprovals(all);

      computeKPIs(all);
      computeTrends(all);
      computeResponseTimes(all);
      computeRejections(all);
      computeCustomerStats(all);
    } catch (err) {
      console.error('Analytics fetch error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const computeKPIs = (all) => {
    const approved = all?.filter(a => a?.status === 'approved');
    const rejected = all?.filter(a => a?.status === 'rejected');
    const pending = all?.filter(a => a?.status === 'pending');
    const decided = [...approved, ...rejected];
    const approvalRate = decided?.length > 0 ? Math.round((approved?.length / decided?.length) * 100) : 0;
    const rejectionRate = decided?.length > 0 ? Math.round((rejected?.length / decided?.length) * 100) : 0;

    const responseTimes = decided?.filter(a => a?.decided_at && a?.created_at)?.map(a => (new Date(a.decided_at) - new Date(a.created_at)) / (1000 * 60 * 60));
    const avgHours = responseTimes?.length > 0
      ? responseTimes?.reduce((s, v) => s + v, 0) / responseTimes?.length
      : null;
    const avgResponseTime = avgHours !== null
      ? avgHours < 24 ? `${Math.round(avgHours)}h` : `${(avgHours / 24)?.toFixed(1)}d`
      : '—';

    setKpis({
      approvalRate,
      rejectionRate,
      avgResponseTime,
      total: all?.length,
      totalDecided: decided?.length,
      totalRejected: rejected?.length,
      pending: pending?.length,
    });
  };

  const computeTrends = (all) => {
    // Group by week
    const weeks = {};
    all?.forEach(a => {
      const d = new Date(a.created_at);
      const weekStart = new Date(d);
      weekStart?.setDate(d?.getDate() - d?.getDay());
      const key = weekStart?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!weeks?.[key]) weeks[key] = { label: key, approved: 0, rejected: 0, pending: 0 };
      if (a?.status === 'approved') weeks[key].approved++;
      else if (a?.status === 'rejected') weeks[key].rejected++;
      else weeks[key].pending++;
    });
    setTrendData(Object.values(weeks)?.slice(-8));
  };

  const computeResponseTimes = (all) => {
    const decided = all?.filter(a => a?.decided_at && a?.created_at);
    const buckets = [
      { range: '< 1hr', min: 0, max: 1, count: 0 },
      { range: '1-4hrs', min: 1, max: 4, count: 0 },
      { range: '4-24hrs', min: 4, max: 24, count: 0 },
      { range: '1-3days', min: 24, max: 72, count: 0 },
      { range: '3+ days', min: 72, max: Infinity, count: 0 },
    ];
    decided?.forEach(a => {
      const hours = (new Date(a.decided_at) - new Date(a.created_at)) / (1000 * 60 * 60);
      const bucket = buckets?.find(b => hours >= b?.min && hours < b?.max);
      if (bucket) bucket.count++;
    });
    setResponseTimeData(buckets);
  };

  const computeRejections = (all) => {
    const rejected = all?.filter(a => a?.status === 'rejected');
    // Categorize by reason keywords or type
    const reasons = {};
    rejected?.forEach(a => {
      const note = (a?.rejection_reason ?? '')?.toLowerCase();
      let category = 'No reason provided';
      if (note?.includes('price') || note?.includes('cost') || note?.includes('expensive')) category = 'Price concerns';
      else if (note?.includes('time') || note?.includes('schedule') || note?.includes('date')) category = 'Scheduling conflict';
      else if (note?.includes('scope') || note?.includes('service') || note?.includes('work')) category = 'Scope disagreement';
      else if (note?.includes('cancel') || note?.includes('no longer')) category = 'Cancelled request';
      else if (note?.trim()) category = 'Other reasons';
      reasons[category] = (reasons?.[category] ?? 0) + 1;
    });
    setRejectionData(
      Object.entries(reasons)?.map(([name, count]) => ({ name, count }))?.sort((a, b) => b?.count - a?.count)
    );
  };

  const computeCustomerStats = (all) => {
    const byCustomer = {};
    all?.forEach(a => {
      const name = a?.customer_name ?? 'Unknown';
      if (!byCustomer?.[name]) byCustomer[name] = { customer: name, total: 0, approved: 0, rejected: 0, responseTimes: [] };
      byCustomer[name].total++;
      if (a?.status === 'approved') byCustomer[name].approved++;
      if (a?.status === 'rejected') byCustomer[name].rejected++;
      if (a?.decided_at && a?.created_at) {
        byCustomer?.[name]?.responseTimes?.push(
          (new Date(a.decided_at) - new Date(a.created_at)) / (1000 * 60 * 60)
        );
      }
    });
    const rows = Object.values(byCustomer)?.map(c => {
        const decided = c?.approved + c?.rejected;
        const rate = decided > 0 ? Math.round((c?.approved / decided) * 100) : 0;
        const avgH = c?.responseTimes?.length > 0
          ? c?.responseTimes?.reduce((s, v) => s + v, 0) / c?.responseTimes?.length
          : null;
        const avgResponse = avgH !== null
          ? avgH < 24 ? `${Math.round(avgH)}h` : `${(avgH / 24)?.toFixed(1)}d`
          : '—';
        return { ...c, rate, avgResponse };
      })?.sort((a, b) => b?.total - a?.total)?.slice(0, 20);
    setCustomerData(rows);
  };

  const exportCSV = () => {
    if (!rawApprovals?.length) return;
    const headers = ['Type', 'Customer', 'Status', 'Created At', 'Decided At', 'Response Time (hrs)'];
    const rows = rawApprovals?.map(a => {
      const responseHrs = a?.decided_at && a?.created_at
        ? ((new Date(a.decided_at) - new Date(a.created_at)) / (1000 * 60 * 60))?.toFixed(1)
        : '';
      return [
        a?.type,
        a?.customer_name,
        a?.status,
        a?.created_at ? new Date(a.created_at)?.toLocaleDateString() : '',
        a?.decided_at ? new Date(a.decided_at)?.toLocaleDateString() : '',
        responseHrs,
      ];
    });
    const csv = [headers, ...rows]?.map(r => r?.map(v => `"${v ?? ''}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `approval-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
    showSuccess('CSV exported successfully');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8 px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Approval Analytics & Reports</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Comprehensive metrics and trend analysis for customer approval workflows
            </p>
          </div>
          <div className="flex items-center gap-3">
            {successMsg && (
              <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 text-success text-sm font-medium rounded-lg">
                <Icon name="CheckCircle" size={14} />
                {successMsg}
              </div>
            )}
            {/* Date Range */}
            <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg">
              {DATE_RANGES?.map(r => (
                <button
                  key={r?.id}
                  onClick={() => setDateRange(r?.id)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    dateRange === r?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {r?.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={exportCSV}>
              <Icon name="Download" size={14} className="mr-1.5" />
              Export CSV
            </Button>
            <Button size="sm" onClick={fetchAnalytics}>
              <Icon name="RefreshCw" size={14} className="mr-1.5" />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <ApprovalKPICards kpis={kpis} loading={loading} />

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-xl mb-5 overflow-x-auto">
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ApprovalTrendChart data={trendData} loading={loading} />
            <ResponseTimeChart data={responseTimeData} loading={loading} />
            <div className="lg:col-span-2">
              <RejectionAnalysisPanel
                data={rejectionData}
                loading={loading}
                totalRejected={kpis?.totalRejected ?? 0}
              />
            </div>
          </div>
        )}

        {activeTab === 'trends' && (
          <ApprovalTrendChart data={trendData} loading={loading} />
        )}

        {activeTab === 'response' && (
          <ResponseTimeChart data={responseTimeData} loading={loading} />
        )}

        {activeTab === 'rejections' && (
          <RejectionAnalysisPanel
            data={rejectionData}
            loading={loading}
            totalRejected={kpis?.totalRejected ?? 0}
          />
        )}

        {activeTab === 'customers' && (
          <div className="bg-card border border-border rounded-xl">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-semibold text-foreground">Customer Approval Behavior</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Per-customer approval rates and response patterns</p>
            </div>
            <CustomerApprovalTable data={customerData} loading={loading} />
          </div>
        )}
      </main>
    </div>
  );
};

export default ApprovalAnalyticsReports;
